import React, {useState, useRef, useCallback} from 'react';
import {
  View,
  StyleSheet,
  FlatList,
  Dimensions,
  TouchableOpacity,
} from 'react-native';
import {Text} from 'react-native-paper';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import {useTranslation} from 'react-i18next';
import {onboardingColors} from '../../theme/onboardingColors';

const {width} = Dimensions.get('window');

const slides = [
  {icon: 'human-male-height', titleKey: 'slide1Title', subtitleKey: 'slide1Subtitle', bg: '#E8F5E9', accent: '#2E7D32'},
  {icon: 'clipboard-list-outline', titleKey: 'slide2Title', subtitleKey: 'slide2Subtitle', bg: '#E3F2FD', accent: '#1565C0'},
  {icon: 'whatsapp', titleKey: 'slide3Title', subtitleKey: 'slide3Subtitle', bg: '#E8F5E9', accent: '#128C7E'},
  {icon: 'chart-line', titleKey: 'slide4Title', subtitleKey: 'slide4Subtitle', bg: '#FFF3E0', accent: '#E65100'},
];

const OnboardingScreen = ({onDone}) => {
  const {t} = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const flatListRef = useRef(null);

  const onViewableItemsChanged = useRef(({viewableItems}) => {
    if (viewableItems.length > 0) {
      setCurrentIndex(viewableItems[0].index);
    }
  }).current;

  const viewabilityConfig = useRef({viewAreaCoveragePercentThreshold: 50}).current;

  const handleNext = useCallback(() => {
    if (currentIndex < slides.length - 1) {
      flatListRef.current?.scrollToIndex({index: currentIndex + 1});
    } else {
      onDone();
    }
  }, [currentIndex, onDone]);

  const renderSlide = ({item}) => (
    <View style={styles.slide}>
      <View style={[styles.iconContainer, {backgroundColor: item.bg}]}>
        <Icon name={item.icon} size={64} color={item.accent} />
      </View>
      <Text style={styles.slideTitle}>{t(`onboarding.${item.titleKey}`)}</Text>
      <Text style={styles.slideSubtitle}>
        {t(`onboarding.${item.subtitleKey}`)}
      </Text>
    </View>
  );

  const isLastSlide = currentIndex === slides.length - 1;

  return (
    <View style={styles.container}>
      <TouchableOpacity style={styles.skipButton} onPress={onDone}>
        <Text style={styles.skipText}>{t('onboarding.skip')}</Text>
      </TouchableOpacity>

      <FlatList
        ref={flatListRef}
        data={slides}
        renderItem={renderSlide}
        keyExtractor={(_, index) => index.toString()}
        horizontal
        pagingEnabled
        showsHorizontalScrollIndicator={false}
        onViewableItemsChanged={onViewableItemsChanged}
        viewabilityConfig={viewabilityConfig}
        bounces={false}
      />

      <View style={styles.footer}>
        <View style={styles.dots}>
          {slides.map((_, index) => (
            <View
              key={index}
              style={[
                styles.dot,
                index === currentIndex ? styles.dotActive : null,
              ]}
            />
          ))}
        </View>

        <TouchableOpacity style={styles.nextButton} onPress={handleNext}>
          <Text style={styles.nextButtonText}>
            {isLastSlide
              ? t('onboarding.getStarted')
              : t('onboarding.next')}
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: onboardingColors.background,
  },
  skipButton: {
    position: 'absolute',
    top: 50,
    right: 20,
    zIndex: 10,
    padding: 8,
  },
  skipText: {
    fontSize: 16,
    color: onboardingColors.textSecondary,
  },
  slide: {
    width,
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 40,
  },
  iconContainer: {
    width: 120,
    height: 120,
    borderRadius: 60,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 32,
  },
  slideTitle: {
    fontSize: 24,
    fontWeight: '800',
    color: onboardingColors.text,
    textAlign: 'center',
    marginBottom: 12,
  },
  slideSubtitle: {
    fontSize: 15,
    color: onboardingColors.textSecondary,
    textAlign: 'center',
    maxWidth: 300,
    lineHeight: 23,
  },
  footer: {
    paddingBottom: 50,
    alignItems: 'center',
  },
  dots: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 30,
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: onboardingColors.dot,
    marginHorizontal: 4,
  },
  dotActive: {
    width: 24,
    backgroundColor: onboardingColors.dotActive,
    borderRadius: 4,
  },
  nextButton: {
    backgroundColor: onboardingColors.primary,
    paddingHorizontal: 48,
    paddingVertical: 14,
    borderRadius: 28,
  },
  nextButtonText: {
    color: onboardingColors.white,
    fontSize: 16,
    fontWeight: '600',
  },
});

export default OnboardingScreen;
